import { useEffect, useMemo, useState } from "react";
import genieSvgUrl from "/genie.svg";
import { DEFAULT_BRANDING } from "@/types/branding";
import { getAvatarCircleBackground } from "@/lib/avatarBackground";

const svgCache = new Map<string, string>();
let svgFetchPromise: Promise<string> | null = null;

interface Props {
  color?: string; // brand colour
  speaking?: boolean; // true while TTS is active
  listening?: boolean; // true while listening for input
  thinking?: boolean; // true while processing/thinking (brief)
  processing?: boolean; // true while processing (medium duration)
  searching?: boolean; // true while searching knowledge (longer duration)
  retrieving?: boolean; // true while retrieving (very long duration)
  isConnecting?: boolean; // true while attempting to connect
  size?: number;
  customAvatarUrl?: string | null; // custom avatar image URL
  /** Full-bleed avatar photo — fills the entire circle (object-fit: cover)
   *  instead of floating as a logo mark. Takes precedence over customAvatarUrl. */
  coverImageUrl?: string | null;
  /** When true and both coverImageUrl and customAvatarUrl are set, shows the
   *  genie logo as a small badge on top of the cover photo. */
  showLogoOverlay?: boolean;
  secondaryColor?: string | null; // secondary color for gradients
  gradientEnabled?: boolean;
  gradientAngle?: number;
  hasCustomBranding?: boolean; // indicates if genie has custom branding configured
  /** Primary-colour strength of the avatar background, 0–100 (100 = solid).
   *  Unset keeps the default light tint. See GenieBranding.avatarBackgroundOpacity. */
  avatarBackgroundOpacity?: number | null;
}

// --- Animated Genie Avatar ---
export const GenieAvatar = ({
  color = DEFAULT_BRANDING.primaryColor,
  speaking = false,
  listening = false,
  thinking = false,
  processing = false,
  searching = false,
  retrieving = false,
  isConnecting = false,
  size = 320,
  customAvatarUrl = null,
  coverImageUrl = null,
  showLogoOverlay = false,
  secondaryColor = null,
  gradientEnabled = false,
  gradientAngle = 135,
  hasCustomBranding = false,
  avatarBackgroundOpacity = null,
}: Props) => {
  const [svgContent, setSvgContent] = useState<string>("");
  const [baseSvg, setBaseSvg] = useState<string>("");
  const [gradientId] = useState(`genie-gradient-${Math.random().toString(36).substr(2, 9)}`);
  const [viewportWidth, setViewportWidth] = useState<number | null>(
    typeof window !== "undefined" ? window.innerWidth : null
  );
  // Hides the logo overlay badge if its image URL fails to load — the
  // browser's default broken-image glyph ignores objectFit/centering CSS
  // entirely, so a bad URL would otherwise render as a stray icon that
  // looks "off-center" no matter how the positioning math is tuned.
  const [logoOverlayFailed, setLogoOverlayFailed] = useState(false);
  useEffect(() => {
    setLogoOverlayFailed(false);
  }, [customAvatarUrl]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const effectiveSize = useMemo(() => {
    if (!viewportWidth) return size;
    // Mobile-first: keep large avatars from dominating the screen width
    // Note: we only apply this to "large" avatars so small list avatars (e.g. 24/48/64) are unaffected.
    if (viewportWidth < 640 && size >= 200) {
      const MOBILE_MAX_RATIO = 0.55;
      const MOBILE_MAX_PX = 280;
      return Math.round(Math.min(size, viewportWidth * MOBILE_MAX_RATIO, MOBILE_MAX_PX));
    }
    return size;
  }, [size, viewportWidth]);

  const customAvatarScale =
    customAvatarUrl && viewportWidth !== null && viewportWidth < 640 ? 0.72 : 0.55;
  const mobileInnerCircleScale =
    customAvatarUrl && viewportWidth !== null && viewportWidth < 640 ? 1.14 : 1;

  useEffect(() => {
    if (!customAvatarUrl && !baseSvg) {
      const cacheKey = genieSvgUrl;

      if (svgCache.has(cacheKey)) {
        const cachedSvg = svgCache.get(cacheKey)!;
        setBaseSvg(cachedSvg);
        return;
      }

      if (svgFetchPromise) {
        svgFetchPromise.then(setBaseSvg).catch((error) => {
          console.error("Failed to load genie SVG:", error);
        });
        return;
      }

      svgFetchPromise = fetch(genieSvgUrl)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to load SVG: ${response.status}`);
          }
          return response.text();
        })
        .then((text) => {
          svgCache.set(cacheKey, text);
          svgFetchPromise = null;
          return text;
        })
        .catch((error) => {
          svgFetchPromise = null;
          throw error;
        });

      svgFetchPromise.then(setBaseSvg).catch((error) => {
        console.error("Failed to load genie SVG:", error);
      });
    }
  }, [customAvatarUrl, baseSvg]);

  useEffect(() => {
    if (!baseSvg) return;
    let processedSvg = baseSvg;

    if (gradientEnabled && secondaryColor) {
      // Create a gradient definition
      const angle = gradientAngle || 135;
      const rad = (angle * Math.PI) / 180;
      const x1 = 50 + 50 * Math.cos(rad + Math.PI);
      const y1 = 50 + 50 * Math.sin(rad + Math.PI);
      const x2 = 50 + 50 * Math.cos(rad);
      const y2 = 50 + 50 * Math.sin(rad);

      const gradientDef = `
        <defs>
          <linearGradient id="${gradientId}" x1="${x1}%" y1="${y1}%" x2="${x2}%" y2="${y2}%">
            <stop offset="0%" style="stop-color:${color};stop-opacity:1" />
            <stop offset="100%" style="stop-color:${secondaryColor};stop-opacity:1" />
          </linearGradient>
        </defs>
      `;

      processedSvg = processedSvg.replace(/<svg[^>]*>/, (match) => match + gradientDef);

      processedSvg = processedSvg.replace(/fill="#[0-9a-fA-F]{6}"/g, `fill="url(#${gradientId})"`);
    } else {
      processedSvg = processedSvg.replace(/fill="#[0-9a-fA-F]{6}"/g, `fill="${color}"`);
    }

    setSvgContent(processedSvg);
  }, [baseSvg, color, secondaryColor, gradientEnabled, gradientAngle, gradientId]);

  // Branded background for the avatar circle — strength follows the genie's
  // avatarBackgroundOpacity setting (legacy 28% tint when unset).
  const backgroundStyle = getAvatarCircleBackground({
    color,
    secondaryColor,
    gradientEnabled,
    gradientAngle,
    opacity: avatarBackgroundOpacity,
  });

  return (
    <div
      className={`relative flex items-center justify-center rounded-full overflow-visible ${isConnecting && !speaking && !listening ? "genie-connecting-active" : ""
        }`}
      style={{ "--glow": color, width: effectiveSize, height: effectiveSize } as React.CSSProperties}
    >
      {/* Animated pulsing radial background */}
      <div
        className="absolute inset-0 rounded-full z-0 genie-bg"
        style={{
          // Branded soft radial background behind the avatar
          background: backgroundStyle,
          transform: `scale(${mobileInnerCircleScale})`,
        }}
      />
      {/* Multi-layer animated glowing outline */}
      <div
        className={`absolute inset-0 z-10 pointer-events-none genie-glow-outer ${
          speaking ? "genie-glow-outer-speaking" :
          listening ? "genie-glow-outer-listening" :
          retrieving ? "genie-glow-outer-retrieving" :
          searching ? "genie-glow-outer-searching" :
          processing ? "genie-glow-outer-processing" :
          thinking ? "genie-glow-outer-thinking" : ""
        }`}
      />
      {/* Genie SVG Head */}
      {coverImageUrl ? (
        <div
          style={{
            width: effectiveSize,
            height: effectiveSize,
            zIndex: 20,
            borderRadius: "50%",
            overflow: "hidden",
            transform: speaking ? "scale(1.006)" : listening ? "scale(1.003)" : "scale(1)",
            transition: "transform 0.3s ease-out",
          }}
          className="relative"
        >
          <img
            src={coverImageUrl}
            alt="Genie Avatar"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
          {showLogoOverlay && customAvatarUrl && !logoOverlayFailed && (
            // Logo overlaid dead-center on top of the cover photo — not a
            // corner badge. Sized well within the circle so the photo still
            // reads as the frame around it.
            <img
              src={customAvatarUrl}
              alt="Genie Logo"
              onError={() => setLogoOverlayFailed(true)}
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: effectiveSize * 0.4,
                height: effectiveSize * 0.4,
                objectFit: "contain",
                filter: "drop-shadow(0 1px 4px rgba(0,0,0,0.55))",
              }}
            />
          )}
        </div>
      ) : customAvatarUrl ? (
        <div
          style={{
            width: effectiveSize * customAvatarScale,
            height: effectiveSize * customAvatarScale,
            zIndex: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transform: speaking ? "scale(1.006)" : listening ? "scale(1.003)" : "scale(1)",
            transition: "transform 0.3s ease-out",
          }}
          className="relative"
        >
          <img
            src={customAvatarUrl}
            alt="Genie Head"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              filter: "none",
              mixBlendMode: "normal",
            }}
          />
        </div>
      ) : svgContent ? (
        <div
          style={{
            width: effectiveSize * 0.9,
            height: effectiveSize * 0.9,
            zIndex: 20,
            filter: speaking ? `drop-shadow(0 0 32px ${color})` : "none",
            transition: "filter 0.1s ease-out",
          }}
          className="relative flex items-center justify-center genie-avatar-svg"
          dangerouslySetInnerHTML={{ __html: svgContent }}
        />
      ) : (
        <div style={{ width: effectiveSize * 0.9, height: effectiveSize * 0.9, zIndex: 20 }} />
      )}
      {/* Sparkle effect removed */}
      <style>{`
        .genie-avatar-svg {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .genie-avatar-svg svg {
          width: 100%;
          height: 100%;
          max-width: 100%;
          max-height: 100%;
        }
        .genie-bg {
          transition: background 0.5s;
        }
        .genie-bg-speaking {
          animation: geniePulse 1.5s infinite cubic-bezier(0.4,0,0.2,1);
        }
        @keyframes geniePulse {
          0% { filter: blur(0px) brightness(1); }
          50% { filter: blur(8px) brightness(1.15); }
          100% { filter: blur(0px) brightness(1); }
        }
        .genie-glow-outer {
          border-radius: 50%;
          border: 8px solid transparent;
          box-shadow: 0 0 0 0 transparent;
          transition: box-shadow 0.1s ease-out; // Faster transition
        }
        .genie-glow-outer-speaking {
          box-shadow: 0 0 48px 8px var(--glow, #4E9CFF), 0 0 120px 24px var(--glow, #4E9CFF88);
          border: 8px solid var(--glow, #4E9CFF);
          animation: genieGlowOuter 1.5s infinite alternate;
        }
        @keyframes genieGlowOuter {
          0% { box-shadow: 0 0 36px 6px var(--glow, #4E9CFF), 0 0 90px 18px var(--glow, #4E9CFF88); }
          100% { box-shadow: 0 0 60px 18px var(--glow, #FF4DB8), 0 0 140px 36px var(--glow, #FF4DB888); }
        }
        /* Inner glow removed for cleaner look */
                /* Sparkle CSS removed */
        /* Removed extra brace here */
        .genie-connecting-active::before {
          content: '';
          position: absolute;
          left: -10px; /* Corresponds to border thickness */
          top: -10px;
          right: -10px;
          bottom: -10px;
          border-radius: 50%;
          border: 10px solid transparent; /* Thicker border (10px), mostly transparent */
          border-top-color: var(--glow, #4E9CFF); /* Spinner color - 1st quadrant */
          border-right-color: var(--glow, #4E9CFF); /* Spinner color - 2nd quadrant, making it a semi-circle */
          animation: genieSpinner 0.8s linear infinite; /* Slightly faster spin */
          z-index: 1; /* Above parent's direct background, below other elements like text/image if any */
        }
        @keyframes genieSpinner {
          to {
            transform: rotate(360deg);
          }
        }

        /* Listening Animations */
        .genie-bg-listening {
          animation: genieListenPulse 2s infinite ease-in-out;
        }
        @keyframes genieListenPulse {
          0%, 100% { transform: scale(1); opacity: 0.8; } /* Base opacity slightly higher */
          50% { transform: scale(0.85); opacity: 0.2; } /* Contract more, become more transparent */
        }

        .genie-glow-outer-listening {
          border: 6px solid var(--glow);
          box-shadow: 0 0 20px 4px var(--glow);
          opacity: 0.3;
          animation: genieListenGlowOuterAnim 2.5s infinite alternate ease-in-out;
        }
        @keyframes genieListenGlowOuterAnim {
          0% { opacity: 0.1; transform: scale(1); } /* Start more subtle */
          100% { opacity: 0.7; transform: scale(1.12); } /* Expand more, become more opaque */
        }

        /* Thinking Animation - gentle pulsing */
        .genie-glow-outer-thinking {
          border: 4px solid var(--glow);
          box-shadow: 0 0 16px 3px var(--glow);
          opacity: 0.5;
          animation: genieThinkingPulse 1s infinite ease-in-out;
        }
        @keyframes genieThinkingPulse {
          0%, 100% {
            opacity: 0.3;
            box-shadow: 0 0 12px 2px var(--glow);
          }
          50% {
            opacity: 0.7;
            box-shadow: 0 0 24px 6px var(--glow);
          }
        }

        /* Processing Animation - rotating glow */
        .genie-glow-outer-processing {
          border: 5px solid var(--glow);
          box-shadow: 0 0 20px 4px var(--glow);
          animation: genieProcessingRotate 2s linear infinite;
        }
        @keyframes genieProcessingRotate {
          0% { box-shadow: 0 0 20px 4px var(--glow), 20px 0 30px 0 var(--glow); }
          25% { box-shadow: 0 0 20px 4px var(--glow), 0 20px 30px 0 var(--glow); }
          50% { box-shadow: 0 0 20px 4px var(--glow), -20px 0 30px 0 var(--glow); }
          75% { box-shadow: 0 0 20px 4px var(--glow), 0 -20px 30px 0 var(--glow); }
          100% { box-shadow: 0 0 20px 4px var(--glow), 20px 0 30px 0 var(--glow); }
        }

        /* Searching Animation - expanding ripple */
        .genie-glow-outer-searching {
          border: 4px solid var(--glow);
          animation: genieSearchingRipple 1.5s ease-out infinite;
        }
        @keyframes genieSearchingRipple {
          0% {
            box-shadow: 0 0 0 0 var(--glow), 0 0 0 0 var(--glow);
            opacity: 0.8;
          }
          50% {
            box-shadow: 0 0 10px 10px transparent, 0 0 20px 20px var(--glow);
            opacity: 0.5;
          }
          100% {
            box-shadow: 0 0 20px 20px transparent, 0 0 40px 40px transparent;
            opacity: 0.3;
          }
        }

        /* Retrieving Animation - intense pulsing */
        .genie-glow-outer-retrieving {
          border: 6px solid var(--glow);
          box-shadow: 0 0 24px 6px var(--glow);
          animation: genieRetrievingPulse 0.8s ease-in-out infinite;
        }
        @keyframes genieRetrievingPulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.6;
            box-shadow: 0 0 20px 4px var(--glow);
          }
          50% {
            transform: scale(1.03);
            opacity: 1;
            box-shadow: 0 0 32px 10px var(--glow);
          }
        }

      `}</style>
    </div>
  );
};
